import React from 'react';
import { TableCell, TableRow, Typography, Chip, Box, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { styled } from '@mui/material/styles';

import { formatMoney, formatBrDate } from '../../shared/formatters';
import { goTo } from '../../shared/utils';

const GroupRow = styled(TableRow)(({ theme }) => ({
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
        backgroundColor: 'rgba(0, 98, 105, 0.08)',
    },
}));

const CategoryRow = styled(TableRow)(({ theme }) => ({
    cursor: 'pointer',
    backgroundColor: 'rgba(0, 98, 105, 0.02)',
    transition: 'background-color 0.2s ease',
    '&:hover': {
        backgroundColor: 'rgba(0, 98, 105, 0.06)',
    },
}));

const TransactionRow = styled(TableRow)(({ theme }) => ({
    cursor: 'pointer',
    backgroundColor: '#fafafa',
    transition: 'background-color 0.2s ease',
    '&:hover': {
        backgroundColor: 'rgba(0, 98, 105, 0.04)',
    },
}));

const GroupCell = styled(TableCell)(({ theme }) => ({
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '0.938rem',
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
    position: 'sticky',
    left: 0,
    backgroundColor: 'white',
    zIndex: 2,
}));

const CategoryCell = styled(TableCell)(({ theme }) => ({
    cursor: 'pointer',
    fontSize: '0.938rem',
    fontWeight: 600,
    color: '#334155',
    paddingLeft: theme.spacing(4),
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
    position: 'sticky',
    left: 0,
    backgroundColor: '#fbfcfc',
    zIndex: 2,
}));

const TransactionCell = styled(TableCell)(({ theme }) => ({
    fontSize: '0.875rem',
    fontWeight: 400,
    color: '#475569',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(7),
    borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
    position: 'sticky',
    left: 0,
    backgroundColor: '#fafafa',
    zIndex: 2,
}));

const TransactionDate = styled('span')(({ theme }) => ({
    fontWeight: 600,
    color: 'rgb(0,98,105)',
    marginRight: theme.spacing(1),
    whiteSpace: 'nowrap',
}));

const TransactionAccount = styled('span')(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const MoneyCell = styled(TableCell)(({ theme }) => ({
    fontSize: '0.938rem',
    fontWeight: 500,
    padding: '16px',
    whiteSpace: 'nowrap',
    width: '220px',
    minWidth: '220px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
}));

const TransactionMoneyCell = styled(TableCell)(({ theme }) => ({
    fontSize: '0.875rem',
    fontWeight: 600,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingRight: theme.spacing(3),
    whiteSpace: 'nowrap',
    borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
}));

const TransactionCount = styled(Chip)(({ theme }) => ({
    height: '20px',
    fontSize: '0.75rem',
    fontWeight: 600,
    marginLeft: theme.spacing(1),
    backgroundColor: 'rgba(0, 98, 105, 0.1)',
    color: 'rgb(0,98,105)',
}));

const EmptyMessage = styled(Typography)(({ theme }) => ({
    fontSize: '0.813rem',
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
}));

const TrendIndicator = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '130px',
    fontSize: '0.75rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary,
    opacity: 0.9,
}));

const MoneyLine = styled(Box)({
    display: 'grid',
    gridTemplateColumns: '130px 120px 62px',
    justifyContent: 'end',
    alignItems: 'center',
    columnGap: '3px',
    textAlign: 'right',
    width: '100%',
});

const PercentageLabel = styled('span', {
    shouldForwardProp: (prop) => prop !== 'isGood',
})(({ theme, isGood }) => ({
    width: '62px',
    display: 'inline-block',
    textAlign: 'right',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: isGood ? theme.palette.success.dark : theme.palette.error.dark,
}));

const TotalValueLabel = styled('span')({
    display: 'inline-block',
    width: '120px',
    textAlign: 'right',
    whiteSpace: 'nowrap',
});

function isDebitCategory(categoryType) {
    return categoryType === 'debit' || categoryType === 'debit-transfer';
}

/**
 * Normalizes a value so credits and debits are both displayed as positive
 * numbers inside their own sections.
 */
function formatSectionMoney(value) {
    return formatMoney(Math.abs(parseFloat(value) || 0));
}

/**
 * Compares the current value with the previous month.
 * Spending less (debit) or earning more (credit) is considered an improvement.
 */
export function getMonthComparison(currentValue, previousValue, isDebit) {
    const current = Math.abs(parseFloat(currentValue) || 0);
    const previous = Math.abs(parseFloat(previousValue) || 0);
    const difference = current - previous;

    if (current === 0 && previous === 0) return null;
    if (Math.abs(difference) < 0.005) return null;

    const isIncrease = difference > 0;
    const isGood = isDebit ? difference < 0 : difference > 0;

    return {
        difference,
        isIncrease,
        // A ratio from zero has no meaning, so no percentage is shown
        percentage: previous > 0 ? (difference / previous) * 100 : null,
        isGood
    };
}

function formatPercentage(percentage) {
    return `${Math.abs(percentage).toFixed(1).replace('.', ',')}%`;
}

const MoneyWithComparison = ({ value, comparison, previousValue, previousMonthKey }) => {
    const hasMeaningfulComparison = comparison && Math.abs(comparison.difference) >= 0.005;

    return (
        <MoneyLine>
            {hasMeaningfulComparison ? (
                <Tooltip
                    title={`Mês anterior (${previousMonthKey}): ${formatSectionMoney(previousValue)}`}
                    placement="top"
                >
                    <TrendIndicator>
                        {formatSectionMoney(previousValue)}
                    </TrendIndicator>
                </Tooltip>
            ) : (
                <TrendIndicator sx={{ visibility: 'hidden' }}>
                    0
                </TrendIndicator>
            )}
            <TotalValueLabel>{formatSectionMoney(value)}</TotalValueLabel>
            <PercentageLabel isGood={comparison ? comparison.isGood : true}>
                {hasMeaningfulComparison && comparison.percentage !== null
                    ? `(${comparison.isIncrease ? '+' : '-'}${formatPercentage(comparison.percentage)})`
                    : ''}
            </PercentageLabel>
        </MoneyLine>
    );
};

function GroupComponent({
    groupData,
    monthKey,
    previousMonthKey,
    transactionsByCategory,
    navigate,
    isOpen,
    openCategories,
    onToggleGroup,
    onToggleCategory
}) {
    const categories = Array.isArray(groupData.categories)
        ? groupData.categories
        : Object.values(groupData.categories || {});

    const isDebitGroup = isDebitCategory(groupData.group.type);
    const groupComparison = previousMonthKey
        ? getMonthComparison(groupData.balance[monthKey], groupData.balance[previousMonthKey], isDebitGroup)
        : null;

    return (
        <React.Fragment key={groupData.group.id}>
            <GroupRow hover onClick={() => onToggleGroup(groupData.group.id)}>
                <GroupCell component="th" scope="row">
                    {groupData.group.name}
                    {isOpen ? (
                        <RemoveIcon sx={{ fontSize: '1rem', ml: 1, verticalAlign: 'middle' }} />
                    ) : (
                        <AddIcon sx={{ fontSize: '1rem', ml: 1, verticalAlign: 'middle' }} />
                    )}
                </GroupCell>
                <MoneyCell align="right" colSpan={3} sx={{ fontWeight: 700 }}>
                    <MoneyWithComparison
                        value={groupData.balance[monthKey]}
                        comparison={groupComparison}
                        previousValue={groupData.balance[previousMonthKey]}
                        previousMonthKey={previousMonthKey}
                    />
                </MoneyCell>
            </GroupRow>
            {isOpen && categories.map(categoryData => {
                const category = categoryData.category;
                const categoryTransactions = (transactionsByCategory || {})[category.id] || [];
                const isCategoryOpen = !!(openCategories || {})[category.id];
                const isDebit = isDebitCategory(category.type);
                const categoryComparison = previousMonthKey
                    ? getMonthComparison(categoryData.balance[monthKey], categoryData.balance[previousMonthKey], isDebit)
                    : null;

                return (
                    <React.Fragment key={category.id}>
                        <CategoryRow hover onClick={() => onToggleCategory(category.id)}>
                            <CategoryCell scope="row">
                                {category.name}
                                <TransactionCount
                                    label={`${categoryTransactions.length} ${categoryTransactions.length === 1 ? 'transação' : 'transações'}`}
                                    size="small"
                                />
                                {isCategoryOpen ? (
                                    <RemoveIcon sx={{ fontSize: '0.9rem', ml: 1, verticalAlign: 'middle' }} />
                                ) : (
                                    <AddIcon sx={{ fontSize: '0.9rem', ml: 1, verticalAlign: 'middle' }} />
                                )}
                            </CategoryCell>
                            <MoneyCell align="right" colSpan={3}>
                                <MoneyWithComparison
                                    value={categoryData.balance[monthKey]}
                                    comparison={categoryComparison}
                                    previousValue={categoryData.balance[previousMonthKey]}
                                    previousMonthKey={previousMonthKey}
                                />
                            </MoneyCell>
                        </CategoryRow>
                        {isCategoryOpen && categoryTransactions.length > 0 && categoryTransactions.map(transaction => (
                            <TransactionRow
                                key={transaction.id}
                                hover
                                onClick={() => goTo(navigate, `transactions/edit/${transaction.id}`)}
                            >
                                <TransactionCell scope="row">
                                    <TransactionDate>{formatBrDate(transaction.date)}</TransactionDate>
                                    {transaction.description || 'Sem descrição'}
                                    <TransactionAccount>
                                        {transaction.financeAccount ? ` · ${transaction.financeAccount.name}` : ''}
                                    </TransactionAccount>
                                </TransactionCell>
                                <TransactionMoneyCell
                                    align="right"
                                    sx={{ color: isDebit ? 'error.dark' : 'success.dark' }}
                                >
                                    {`${isDebit ? '-' : '+'}${formatMoney(transaction.value)}`}
                                </TransactionMoneyCell>
                            </TransactionRow>
                        ))}
                        {isCategoryOpen && categoryTransactions.length === 0 &&
                            <TransactionRow>
                                <TransactionCell scope="row" colSpan={2}>
                                    <EmptyMessage>Nenhuma transação encontrada para esta categoria</EmptyMessage>
                                </TransactionCell>
                            </TransactionRow>
                        }
                    </React.Fragment>
                );
            })}
        </React.Fragment>
    );
}

export { isDebitCategory, formatSectionMoney };
export default GroupComponent;
